import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from '../../../utils/useSnackbar';
import { setActivePage } from '../../../features/navigation/navigationSlice';
import {
  getOrgsWithBalances,
  topupCredits,
  getCreditHistory,
} from '../../../utils/api/superAdmin_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import PageHeader from '../../Header/PageHeader';
import TableComponent from '../../TableComponent';
import { renderTableCell } from '../../../utils/commonfunctions';
import styles from '../../../styles/component.module.scss';
import adminForm from '../../../styles/admin/adminInlineForms.module.scss';
import pageStyles from '../../../styles/admin/superAdminCredits.module.scss';
import InputBox from '../../FormComponent/InputBox';
import ButtonComponent from '../../customButton';

/** Top-up uses unified credits; single section for Credit Management */
const TOPUP_SECTIONS = [{ value: 'script_creator', label: 'Credits' }];

const SECTION_LABELS = {
  script_creator: 'Script Creator',
  test_launch: 'Test Launch',
  script_manager: 'Script Manager',
};

const BALANCE_CARD_CONFIG = {
  Credits: {
    icon: DescriptionOutlinedIcon,
    accent: '#2563eb',
    bgTint: 'rgba(37, 99, 235, 0.06)',
  },
  'Script Creator': {
    icon: DescriptionOutlinedIcon,
    accent: '#2563eb',
    bgTint: 'rgba(37, 99, 235, 0.06)',
  },
  'Test Launch': {
    icon: RocketLaunchOutlinedIcon,
    accent: '#059669',
    bgTint: 'rgba(5, 150, 105, 0.06)',
  },
  'Script Manager': {
    icon: FolderOutlinedIcon,
    accent: '#7c3aed',
    bgTint: 'rgba(124, 58, 237, 0.06)',
  },
};

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TopUpForm({ section, orgId, onSuccess, disabled, compact, stacked }) {
  const { showSnackbar } = useSnackbar();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = parseInt(amount, 10);
    if (!orgId || !Number.isInteger(num) || num <= 0) {
      showSnackbar('Please enter a valid positive amount', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const response = await topupCredits({
        orgId,
        section,
        amount: num,
        reason: reason.trim() || undefined,
      });
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || 'Credits topped up successfully', 'success');
        setAmount('');
        setReason('');
        onSuccess();
      } else {
        showSnackbar(response.error || 'Top-up failed', 'error');
      }
    } catch (err) {
      showSnackbar(err?.message || 'Top-up failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isStacked = stacked ?? compact;

  const formClass = [
    adminForm.topUpForm,
    isStacked && adminForm.topUpForm_stacked,
    compact && !isStacked && adminForm.topUpForm_compact,
    compact && !isStacked && adminForm.topUpForm_grow,
  ]
    .filter(Boolean)
    .join(' ');

  const amountShell = isStacked
    ? adminForm.topUpAmount_stacked
    : compact
      ? adminForm.topUpAmount_compact
      : adminForm.topUpAmount_default;
  const reasonShell = isStacked
    ? adminForm.topUpReason_stacked
    : compact
      ? adminForm.topUpReason_compact
      : adminForm.topUpReason_default;
  const submitShell = isStacked
    ? adminForm.topUpSubmit_stacked
    : compact
      ? adminForm.topUpSubmit_compact
      : adminForm.topUpSubmit_default;

  return (
    <form className={formClass} onSubmit={handleSubmit}>
      <div className={amountShell}>
        <InputBox
          id="super-admin-credits-amount"
          name="creditsAmount"
          label="Amount"
          type="number"
          placeholder="Enter amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          size="small"
          disabled={disabled}
        />
      </div>
      <div className={reasonShell}>
        <InputBox
          id="super-admin-credits-reason"
          name="creditsReason"
          label="Reason (optional)"
          placeholder="e.g. Trial extension"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          disabled={disabled}
          aria-label="Reason for top-up"
        />
      </div>
      <div className={submitShell}>
        <ButtonComponent
          variant="primary"
          disabled={disabled || submitting}
          loading={submitting}
          ctaText="Top up"
          size={compact ? 'small' : 'medium'}
          className="white bg-blue600"
          startIcon={submitting ? null : <AddIcon fontSize="small" />}
        />
      </div>
    </form>
  );
}

const SuperAdminCredits = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const showSnakbarRef = useRef(showSnackbar);
  showSnakbarRef.current = showSnackbar;
  const activePage = useSelector((state) => state.navigation.activePage);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    dispatch(setActivePage('Credit Management'));
  }, [dispatch]);

  const loadOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const response = await getOrgsWithBalances();
      if (response.status === STATUS.SUCCESS) {
        setOrgs(response.data || []);
        if (!selectedOrgId && response.data?.length) {
          setSelectedOrgId(response.data[0].orgId);
          setSelectedOrg(response.data[0]);
        } else if (selectedOrgId) {
          const found = (response.data || []).find((o) => o.orgId === selectedOrgId);
          setSelectedOrg(found || null);
        }
      } else {
        showSnackbar(response.error || 'Failed to load organizations', 'error');
      }
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load organizations', 'error');
    } finally {
      setLoadingOrgs(false);
    }
  }, [selectedOrgId, showSnackbar]);

  const loadHistory = useCallback(async () => {
    if (!selectedOrgId) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    try {
      const response = await getCreditHistory(selectedOrgId);
      if (response.status === STATUS.SUCCESS) {
        setHistory(response.data || []);
      } else {
        showSnakbarRef.current(response.error || 'Failed to load history', 'error');
      }
    } catch (err) {
      showSnakbarRef.current(err?.message || 'Failed to load history', 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    loadOrgs();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Refresh credits when Script Creator or Script Manager execution completes
  useEffect(() => {
    const onCreditsRefresh = () => {
      loadOrgs();
      loadHistory();
    };
    window.addEventListener('trialUsageRefresh', onCreditsRefresh);
    return () => window.removeEventListener('trialUsageRefresh', onCreditsRefresh);
  }, [loadOrgs, loadHistory]);

  // Refresh when tab becomes visible (e.g. after running execution in another tab)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadOrgs();
        loadHistory();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [loadOrgs, loadHistory]);

  useEffect(() => {
    if (selectedOrgId && orgs.length) {
      const found = orgs.find((o) => o.orgId === selectedOrgId);
      setSelectedOrg(found || null);
    } else {
      setSelectedOrg(null);
    }
  }, [selectedOrgId, orgs]);

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value || '');
  };

  const handleTopUpSuccess = () => {
    loadOrgs();
    loadHistory();
  };

  const breadcrumbItems = [{ label: 'Settings' }, { label: activePage }];

  const BalanceCard = ({ label, remaining, limit, used, format }) => {
    const config = BALANCE_CARD_CONFIG[label];
    const Icon = config?.icon;
    return (
      <div
        className={pageStyles.balanceCard}
        style={{
          borderLeft: config?.accent ? `4px solid ${config.accent}` : undefined,
          backgroundColor: config?.bgTint || '#fff',
        }}
      >
        <div className={pageStyles.balanceHeader}>
          {Icon && (
            <div
              className={pageStyles.balanceIcon}
              style={{ backgroundColor: config?.accent }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </div>
          )}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
          </Typography>
        </div>
        <div className={pageStyles.balanceValueRow}>
          <Typography variant="h5" component="span" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
            {format === 'credits' ? (remaining ?? '—') : (used ?? '—')}
          </Typography>
          <Typography component="span" variant="body2" fontWeight={400} color="text.secondary">
            {format === 'credits' ? 'remaining' : `of ${limit ?? '—'}`}
          </Typography>
        </div>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {format === 'credits' ? `limit: ${limit ?? '—'}, used: ${used ?? '—'}` : `limit: ${limit ?? '—'}`}
        </Typography>
      </div>
    );
  };

  const orgSelector = (
    <div className={pageStyles.orgSelector}>
      <div className={pageStyles.orgIcon}>
        <BusinessIcon sx={{ fontSize: 20 }} />
      </div>
      <FormControl
        size="small"
        sx={{
          minWidth: 260,
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            fontWeight: 500,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: 'primary.main',
            },
          },
        }}
      >
        <InputLabel id="super-admin-org-select-label">Organization</InputLabel>
        <Select
          labelId="super-admin-org-select-label"
          id="super-admin-org-select"
          value={selectedOrgId}
          label="Organization"
          onChange={handleOrgChange}
          disabled={loadingOrgs}
        >
          <MenuItem value="">
            <em>Select organization</em>
          </MenuItem>
          {(orgs || []).map((org) => (
            <MenuItem key={org.orgId} value={org.orgId}>
              {org.orgName || org.orgId}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingOrgs && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      )}
    </div>
  );

  return (
    <Box className={styles['page-container']} sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          breadcrumbItems={breadcrumbItems}
          title="Credit Management"
          description="View balances and top up credits per organization."
          actions={orgSelector}
        />
      </Box>

      <Box
        className={styles['cover-container-col']}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          width: '100%',
          px: 2,
          pt: 2,
          pb: 2,
        }}
      >
        {selectedOrg && !loadingOrgs && (
          <div className={pageStyles.layout}>
            {/* Left card (28%): Current balances + Top up credits */}
            <div className={pageStyles.leftPane}>
              <div className={pageStyles.card}>
                <h2 className={pageStyles.cardTitle}>Current balances</h2>
                <div className={pageStyles.balanceList}>
                  <div className={pageStyles.balanceItem}>
                    <BalanceCard
                      label="Credits"
                      remaining={selectedOrg.remaining}
                      limit={selectedOrg.credit_limit}
                      used={selectedOrg.credits_used}
                      format="credits"
                    />
                  </div>
                </div>
              </div>

              <div className={pageStyles.card}>
                <h2 className={pageStyles.cardTitle}>Top up credits</h2>
                <div className={pageStyles.topUpBody}>
                  {TOPUP_SECTIONS.map(({ value }) => (
                    <TopUpForm
                      key={value}
                      section={value}
                      orgId={selectedOrgId}
                      onSuccess={handleTopUpSuccess}
                      disabled={loadingHistory}
                      compact
                      stacked
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right card (70%): Credit history */}
            <div className={pageStyles.rightPane}>
              <div className={`${pageStyles.card} ${pageStyles.tableCard}`}>
                <h2 className={pageStyles.cardTitle}>Credit history</h2>
                <div className={pageStyles.tableRegion} role="region" aria-label="Credit history table">
                  <TableComponent
                    title=""
                    isHeaderContentVisible={false}
                    hideColumnsVisibilityButton
                    rows={history}
                    columns={[
                      { field: 'created_at', headerName: 'Date', minWidth: 172, flex: 0, renderCell: (params) => renderTableCell(formatDate(params.value)) },
                      { field: 'adjustedBy', headerName: 'Adjusted by', flex: 1, minWidth: 120, valueGetter: (_, row) => row.adjustedByUser?.name || row.adjustedByUser?.email || row.adjusted_by || '—', renderCell: (params) => renderTableCell(params.value) },
                      { field: 'previous_value', headerName: 'Previous', minWidth: 88, flex: 0, renderCell: (params) => renderTableCell(params.value ?? '—') },
                      { field: 'amount_added', headerName: 'Added', minWidth: 88, flex: 0, renderCell: (params) => renderTableCell(params.value ?? '—') },
                      { field: 'new_total', headerName: 'New total', minWidth: 92, flex: 0, renderCell: (params) => renderTableCell(params.value ?? '—') },
                      { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 120, renderCell: (params) => renderTableCell(params.value || '—') },
                    ]}
                    loading={loadingHistory}
                    disablePagination
                    disableFooter
                    idField="id"
                    viewType="creditHistory"
                    customNoData={
                      <div className={pageStyles.noData}>
                        <Typography color="text.secondary" variant="body2">
                          No history for this organization.
                        </Typography>
                      </div>
                    }
                    isData={history.length > 0 || loadingHistory}
                    tableHeight="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {!selectedOrg && !loadingOrgs && orgs.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No organizations with plans found.
          </Typography>
        )}
        {!selectedOrg && !loadingOrgs && orgs.length > 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            Select an organization above to view balances and history.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SuperAdminCredits;
